import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAdminApi, selectCls, btnPrimary, btnGhost, cardCls } from "./useAdminApi";
import { errMsg, fmtDate, slotLabel, timeOptions } from "@/lib/api";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TIMES = timeOptions();
const TimeSelect = ({ value, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={selectCls} data-testid={testid}>
    {TIMES.map((t) => <option key={t} value={t}>{slotLabel(t)}</option>)}
  </select>
);

const WeeklyHours = () => {
  const call = useAdminApi();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { call("get", "/admin/settings").then(setS); }, [call]);
  if (!s) return <div className={cardCls}><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const toggle = (d) => setS({ ...s, closed_days_of_week: s.closed_days_of_week.includes(d) ? s.closed_days_of_week.filter((x) => x !== d) : [...s.closed_days_of_week, d] });
  const save = async () => {
    setBusy(true);
    try {
      setS(await call("put", "/admin/settings", { opening_time: s.opening_time, closing_time: s.closing_time, closed_days_of_week: s.closed_days_of_week }));
      toast.success("Weekly schedule updated — the public calendar now reflects it");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className={cardCls} data-testid="weekly-hours-card">
      <h3 className="text-xl font-semibold">Working days &amp; hours</h3>
      <p className="text-sm text-ink-muted mt-1">Tap a day to open or close it every week.</p>
      <div className="flex flex-wrap gap-2 mt-4">
        {DAYS.map((d, i) => {
          const open = !s.closed_days_of_week.includes(i);
          return <button key={d} type="button" onClick={() => toggle(i)} data-testid={`weekday-toggle-${d.toLowerCase()}`} data-open={open}
            className={`h-11 w-16 rounded-xl border text-sm font-semibold transition-colors ${open ? "bg-teal text-white border-teal" : "bg-slate-50 text-slate-400 border-slate-200 line-through"}`}>{d}</button>;
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4 text-sm">
        <span>Open</span><TimeSelect value={s.opening_time} onChange={(v) => setS({ ...s, opening_time: v })} testid="opening-time-select" />
        <span>to</span><TimeSelect value={s.closing_time} onChange={(v) => setS({ ...s, closing_time: v })} testid="closing-time-select" />
      </div>
      <button onClick={save} disabled={busy} className={`${btnPrimary} mt-5`} data-testid="save-weekly-hours">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save schedule</button>
    </div>
  );
};

const describe = (o) => o.is_full_day_off ? "Full day off" : o.blocked_slots?.length
  ? `Blocked ${slotLabel(o.block_start)} – ${slotLabel(o.block_end)}` : `Custom hours ${slotLabel(o.custom_open_time)} – ${slotLabel(o.custom_close_time)}`;

const Overrides = () => {
  const call = useAdminApi();
  const [list, setList] = useState([]);
  const [f, setF] = useState({ date: "", type: "off", start: "14:00", end: "16:00", reason: "" });
  const load = useCallback(() => call("get", "/admin/overrides").then(setList), [call]);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    const body = { date: f.date, reason: f.reason, is_full_day_off: f.type === "off" };
    if (f.type === "block") Object.assign(body, { block_start: f.start, block_end: f.end });
    if (f.type === "hours") Object.assign(body, { custom_open_time: f.start, custom_close_time: f.end });
    try { await call("post", "/admin/overrides", body); toast.success("Saved — patients can no longer book those times"); setF({ ...f, reason: "" }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => { await call("delete", `/admin/overrides/${id}`); toast.success("Removed"); load(); };
  return (
    <div className={cardCls} data-testid="overrides-card">
      <h3 className="text-xl font-semibold">Leave, holidays &amp; blocked times</h3>
      <form onSubmit={add} className="grid sm:grid-cols-2 gap-2 mt-4">
        <Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="h-10 rounded-xl" data-testid="override-date" />
        <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={selectCls} data-testid="override-type">
          <option value="off">Full day off (leave / holiday)</option>
          <option value="block">Block a time range</option>
          <option value="hours">Custom hours for this day</option>
        </select>
        {f.type !== "off" && (
          <div className="sm:col-span-2 flex items-center gap-2 text-sm">
            <span>{f.type === "block" ? "Block from" : "Open from"}</span>
            <TimeSelect value={f.start} onChange={(v) => setF({ ...f, start: v })} testid="override-start" />
            <span>to</span>
            <TimeSelect value={f.end} onChange={(v) => setF({ ...f, end: v })} testid="override-end" />
          </div>
        )}
        <Input placeholder="Reason (e.g. Surgery, Diwali)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="h-10 rounded-xl sm:col-span-2" data-testid="override-reason" />
        <button type="submit" className={`${btnPrimary} sm:col-span-2`} data-testid="override-add-button">Add to schedule</button>
      </form>
      <ul className="mt-5 divide-y divide-slate-100" data-testid="override-list">
        {list.length === 0 && <li className="text-sm text-ink-muted py-3">No upcoming leave or blocks.</li>}
        {list.map((o) => (
          <li key={o.id} className="py-3 flex items-center justify-between gap-3" data-testid={`override-item-${o.date}`}>
            <div><p className="font-medium text-sm">{fmtDate(o.date)}</p><p className="text-xs text-ink-muted">{describe(o)}{o.reason && ` · ${o.reason}`}</p></div>
            <button onClick={() => remove(o.id)} className={`${btnGhost} text-red-600`} data-testid={`override-delete-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const SchedulePanel = () => (
  <div className="grid lg:grid-cols-2 gap-4 mt-4" data-testid="schedule-panel">
    <WeeklyHours />
    <Overrides />
  </div>
);
